import { adminRoles } from '@/constants';
import { hasAnyOrgRole } from '@/helpers/roles';
import { useAuthStatus, useLogout } from '@/hooks';
import { useAppStore } from '@/stores/app.ts';
import NautSmallLogo from '@nautilusbio/nb-design-system/assets/logo-n.svg';
import NautLogo from '@nautilusbio/nb-design-system/assets/naut-logo-sm.png';
import { Divider } from '@nautilusbio/nb-design-system/Divider';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { Link } from '@tanstack/react-router';
import { clsx } from 'clsx';
import { DynamicIcon } from 'lucide-react/dynamic';
import React, { type FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Panel } from 'react-resizable-panels';
import { useShallow } from 'zustand/react/shallow';

import HelpLink from './components/HelpLink.tsx';
import NavigationLinks from './components/NavigationLinks.tsx';
import OrgSelector from './components/OrgSelector/index.ts';
import RoleSwitcher from './components/RoleSwitcher.tsx';
import UserMenu from './components/UserMenu.tsx';
import {
  SIDEBAR_COLLAPSED_SIZE,
  SIDEBAR_DEFAULT_SIZE,
  SIDEBAR_ICON_CONFIG,
  SIDEBAR_MAX_SIZE,
  SIDEBAR_MIN_SIZE,
  sidebarTextSize,
} from './constants';
import { sidebarLinkClassName } from './linkStyles';
import styles from './styles.module.css';
import type { SidebarProps } from './types';

const Sidebar: FC<SidebarProps> = ({ disabled, links, sidebarRef }) => {
  const { user } = useAuthStatus();

  // Combine all store selectors into one for better performance
  const { isSidebarCollapsed, setIsSidebarCollapsed, setIsSidebarMenuOpen, setIsOrgSelectorOpen, selectedOrg } =
    useAppStore(
      useShallow(state => ({
        isSidebarCollapsed: state.isSidebarCollapsed,
        setIsSidebarCollapsed: state.setIsSidebarCollapsed,
        setIsSidebarMenuOpen: state.setIsSidebarMenuOpen,
        setIsOrgSelectorOpen: state.setIsOrgSelectorOpen,
        selectedOrg: state.selectedOrganization,
      })),
    );

  const { t } = useTranslation('logo');
  const { t: tSidebar } = useTranslation('sidebar');

  const logout = useLogout();

  // Memoize resize handler to avoid creating new function on every render
  const handleResize = useCallback(() => {
    const collapsed = sidebarRef?.current?.isCollapsed() ?? false;
    setIsSidebarCollapsed(collapsed);
  }, [sidebarRef, setIsSidebarCollapsed]);

  const handleLogout = useCallback(() => logout.mutate(), [logout]);

  const toggleHandler = useCallback(() => {
    if (sidebarRef?.current?.isCollapsed()) {
      sidebarRef?.current?.expand();
    } else {
      sidebarRef?.current?.collapse();
    }
    setIsSidebarMenuOpen(false);
    setIsOrgSelectorOpen(false);
  }, [sidebarRef, setIsOrgSelectorOpen, setIsSidebarMenuOpen]);

  // Select logo source directly
  const logoSrc = isSidebarCollapsed ? NautSmallLogo : NautLogo;

  const authLink = useMemo(() => {
    if (!hasAnyOrgRole(user?.assumed_roles, selectedOrg?.id, adminRoles)) return null;

    const { size, strokeWidth } = SIDEBAR_ICON_CONFIG.expanded;
    // Lock, not a key: the key icon now belongs to the role switcher, matching the mock, and two
    // key rows side by side read as a duplicate. Lock matches /auth's own SAML tab icon.
    return (
      <Link aria-label={tSidebar('aut')} className={sidebarLinkClassName} to="/auth">
        <DynamicIcon name="lock" size={size} strokeWidth={strokeWidth} />
        <Text as="span" size={sidebarTextSize}>
          {tSidebar('aut')}
        </Text>
      </Link>
    );
  }, [selectedOrg?.id, tSidebar, user?.assumed_roles]);

  // This branch's own help affordance was a disabled placeholder; main shipped the real
  // one (POR-273), so the placeholder is dropped and HelpLink reused in both slots.
  const helpLink = <HelpLink isSidebarCollapsed={isSidebarCollapsed} />;

  return (
    <Panel
      className={clsx(styles.sidebarContainer, 'sidebar')}
      collapsedSize={SIDEBAR_COLLAPSED_SIZE}
      collapsible
      defaultSize={SIDEBAR_DEFAULT_SIZE}
      id="sidebar-panel"
      maxSize={SIDEBAR_MAX_SIZE}
      minSize={SIDEBAR_MIN_SIZE}
      onResize={handleResize}
      panelRef={sidebarRef}
    >
      <Stack as="nav" className={clsx(styles.sidebarContent, !user && styles.sidebarNoContent)} orientation="vertical">
        <div
          className={clsx(styles.logoContainer, isSidebarCollapsed && styles.logoContainerIsCollapsed, 'logo-contain')}
        >
          <img
            alt={t('alt')}
            className={clsx(styles.sidebarLogo, isSidebarCollapsed && styles.sidebarLogoIsCollapsed)}
            src={logoSrc}
            title={t('alt')}
          />
        </div>

        {!disabled && (
          <NavigationLinks
            links={links}
            user={user ?? undefined}
            isSidebarCollapsed={isSidebarCollapsed}
            orgId={selectedOrg?.id}
          />
        )}
      </Stack>

      {/* Grouped so sidebarContainer's space-between only splits nav content vs. this footer block, not each child individually */}
      <div className={styles.footerGroup}>
        <Stack
          alignItems="start"
          className={clsx(styles.sidebarFooter, isSidebarCollapsed && styles.sidebarFooterIsCollapsed)}
          fullWidth
          gap={1}
          justifyContent={isSidebarCollapsed ? 'start' : 'space-between'}
          orientation="vertical"
        >
          {!isSidebarCollapsed && helpLink}

          {selectedOrg && !isSidebarCollapsed && (
            <>
              <OrgSelector selectedOrg={selectedOrg} />
              <RoleSwitcher isSidebarCollapsed={isSidebarCollapsed} selectedOrg={selectedOrg} />
              {authLink}
            </>
          )}
        </Stack>

        <Divider className={styles.helpOrgDivider} />

        <UserMenu
          authLink={authLink}
          helpButton={helpLink}
          isLoggingOut={logout.isPending}
          isSidebarCollapsed={isSidebarCollapsed}
          onLogout={handleLogout}
          selectedOrg={selectedOrg}
          toggleHandler={toggleHandler}
        />
      </div>
    </Panel>
  );
};

export default Sidebar;
